import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
  Button, ConfirmDialog,
} from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import CaseForm from '../../components/common/CaseForm.jsx';
import { patientName } from '../../lib/caseForm.js';
import { deletionCounts } from './Patients.shared.js';

// The two dialogs the list raises, shared by both surfaces.
//
// A `Dialog` on both, deliberately, rather than a `BottomSheet` on the phone:
// this is a FORM with eleven fields, and a sheet that covers 85% of the screen
// while the keyboard covers another 40% leaves a two-field window to type in.
// The kit's dialog is already `max-h-[90vh] overflow-y-auto`, which is the
// behaviour a long form needs on either device.

/** «بیمار جدید» / «ویرایش بیمار» — the same form, a different verb. */
export function CaseFormDialog({
  open,
  onClose,
  values,
  onChange,
  onSubmit,
  saving,
  showTitleError,
  mode = 'create',
}) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent size="lg" layout="frame" data-testid="case-form-dialog">
        <DialogHeader>
          <DialogTitle>{t(isEdit ? 'caseForm.title.edit' : 'caseForm.title.create')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <CaseForm
            values={values}
            onChange={onChange}
            disabled={saving}
            // Status is absent on CREATE: every patient starts `active`, the
            // contract's own default, and asking a student to classify a
            // patient they have not written yet is a decision with no
            // information behind it.
            showStatus={isEdit}
            showTitleError={showTitleError}
          />
        </DialogBody>
        <DialogFooter style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Button onClick={onSubmit} disabled={saving} data-testid="case-form-submit">
            {saving
              ? t('common.actions.saving')
              : t(isEdit ? 'caseForm.submit.edit' : 'caseForm.submit.create')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.actions.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * «این بیمار حذف شود؟» — with the counts, because that is the question.
 *
 * The body names the history entries, the visits and the conversations that go
 * with the patient, says there is no undo, says the پارسه already spent is not
 * refunded, and offers ARCHIVING as the thing the student probably meant
 * (core.md §7.3). Every number comes from the row's own count fields.
 */
export function DeletePatientDialog({ row, onCancel, onConfirm, loading }) {
  const { t, formatNumber } = useTranslation();
  if (!row) return null;
  const counts = deletionCounts(row);

  return (
    <ConfirmDialog
      open={Boolean(row)}
      onOpenChange={(next) => { if (!next) onCancel(); }}
      title={t('patients.delete.title')}
      description={t('patients.delete.body', {
        entries: formatNumber(counts.entries),
        visits: formatNumber(counts.visits),
        threads: formatNumber(counts.threads),
      })}
      confirmLabel={t('patients.delete.confirm')}
      cancelLabel={t('common.actions.cancel')}
      destructive
      loading={loading}
      onConfirm={onConfirm}
      confirmTestId="delete-patient-confirm"
      data-testid="delete-patient-dialog"
    >
      <p style={{ margin: 0, fontSize: 12.5, opacity: 0.8 }}>{patientName(row, t)}</p>
    </ConfirmDialog>
  );
}

export default CaseFormDialog;
